import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";

interface RecentActivityTableProps {
  userId: string;
  limit?: number;
}

export function RecentActivityTable({ userId, limit = 5 }: RecentActivityTableProps) {
  const { data: activities = [], isLoading } = useQuery({
    queryKey: ["recent-quiz-sessions", userId],
    queryFn: async () => {
      // Fetch recent study sessions with topic info
      const { data, error } = await supabase
        .from("study_sessions")
        .select(`
          id,
          topic_id,
          started_at,
          mcqs_attempted,
          activity_type,
          topics (name)
        `)
        .eq("user_id", userId)
        .not("ended_at", "is", null)
        .order("started_at", { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Calculate scores from attempts
      const sessionsWithScores = await Promise.all(
        (data || []).map(async (session) => {
          if (session.topic_id) {
            // First get MCQ IDs for this topic
            const { data: mcqs } = await supabase
              .from("mcqs")
              .select("id")
              .eq("topic_id", session.topic_id);

            const mcqIds = mcqs?.map((m) => m.id) || [];

            if (mcqIds.length > 0) {
              const { data: attempts } = await supabase
                .from("attempts")
                .select("is_correct")
                .eq("user_id", userId)
                .in("mcq_id", mcqIds);

              const correct = attempts?.filter((a) => a.is_correct).length || 0;
              const total = attempts?.length || session.mcqs_attempted || 1;
              const score = Math.round((correct / total) * 100);

              return {
                id: session.id,
                topic: session.topics?.name || "Unknown Topic",
                date: session.started_at,
                score,
                type: session.activity_type,
              };
            }

            return {
              id: session.id,
              topic: session.topics?.name || "Unknown Topic",
              date: session.started_at,
              score: 0,
              type: session.activity_type,
            };
          }

          return {
            id: session.id,
            topic: "General Practice",
            date: session.started_at,
            score: 0,
            type: session.activity_type,
          };
        })
      );

      return sessionsWithScores;
    },
  });

  const getScoreColor = (score: number) => {
    if (score >= 90) return "bg-success text-success-foreground";
    if (score >= 70) return "bg-warning text-warning-foreground";
    return "bg-destructive text-destructive-foreground";
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No recent activity</p>
            <Button asChild variant="link" className="mt-2">
              <Link to="/study">Start practicing</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-[2fr_1fr_1fr_auto] gap-4 pb-2 border-b text-sm font-medium text-muted-foreground">
              <div>QUIZ TOPIC</div>
              <div>DATE</div>
              <div>SCORE</div>
              <div></div>
            </div>

            {activities.map((activity) => (
              <div
                key={activity.id}
                className="grid grid-cols-[2fr_1fr_1fr_auto] gap-4 items-center py-2 hover:bg-muted/50 rounded px-2 transition-colors"
              >
                <div className="font-medium text-foreground truncate">
                  {activity.topic}
                </div>
                <div className="text-sm text-muted-foreground">
                  {format(new Date(activity.date), "MMM dd, yyyy")}
                </div>
                <div>
                  <Badge className={getScoreColor(activity.score)}>
                    {activity.score}%
                  </Badge>
                </div>
                <div>
                  <Button asChild size="sm" variant="ghost">
                    <Link to={`/study`}>
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
